import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  ChevronLeft, BookOpen, Search, Calendar, Users, MessageCircle,
  Coins, Vote, Camera, Heart, Clock, BarChart3, GraduationCap,
  Phone, Home, PartyPopper, Mail, Lightbulb, CheckCircle, Lock
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';

const FEATURES = [
  {
    id: 'events',
    title: 'Family Events',
    icon: Calendar,
    color: 'text-blue-500',
    bgColor: 'bg-blue-500/10',
    category: 'Core Features',
    tier: 'free',
    description: 'Plan and coordinate family gatherings, reunions, and celebrations.',
    howToUse: [
      'Navigate to your family page and click the "Events" tab',
      'Click "Create Event" to start planning a new gathering',
      'Set the date, time, location, and description',
      'Use templates to add tasks and assign them to family members',
      'Track RSVPs as members respond'
    ],
    bestPractices: [
      'Give at least 2-4 weeks notice for major events',
      'Include clear directions and parking info',
      'Create a task list to delegate responsibilities',
      'Send reminders a week before and a day before',
      'Follow up after the event to collect photos and feedback'
    ]
  },
  {
    id: 'members',
    title: 'Family Members',
    icon: Users,
    color: 'text-green-500',
    bgColor: 'bg-green-500/10',
    category: 'Core Features',
    tier: 'free',
    description: 'Manage your family group membership and invitations.',
    howToUse: [
      'Go to the "Members" tab on your family page',
      'Click "Invite Member" to send email invitations',
      'Use "Add Proxy" for family members without email',
      'Approve or decline pending join requests',
      'Click on any member to view their profile'
    ],
    bestPractices: [
      'Start by inviting your immediate family',
      'Add proxy members for elderly relatives',
      'Assign roles to active family members',
      'Keep member information up to date',
      'Reach out personally if invitations go unanswered'
    ]
  },
  {
    id: 'chat',
    title: 'Family Chat',
    icon: MessageCircle,
    color: 'text-purple-500',
    bgColor: 'bg-purple-500/10',
    category: 'Communication',
    tier: 'free',
    description: 'Real-time messaging to stay connected with your family.',
    howToUse: [
      'Click the "Chat" tab on your family page',
      'Type your message in the input field',
      'Press Enter or click Send to post',
      'Messages appear instantly for all family members',
      'Scroll up to view message history'
    ],
    bestPractices: [
      'Use chat for quick, informal communication',
      'Keep important announcements in dedicated sections',
      'Be respectful and inclusive in discussions',
      'Avoid sensitive topics better discussed privately',
      'Check in regularly to stay engaged'
    ]
  },
  {
    id: 'gallery',
    title: 'Photo Gallery',
    icon: Camera,
    color: 'text-pink-500',
    bgColor: 'bg-pink-500/10',
    category: 'Memories',
    tier: 'free',
    description: 'Store, organize, and share family photos and memories.',
    howToUse: [
      'Click "Gallery" in the family page header',
      'Use the upload button to add new photos',
      'Create albums to organize photos by event or theme',
      'Click any photo to view it in full size',
      'Use the download button to save photos locally'
    ],
    bestPractices: [
      'Organize photos into albums right after events',
      'Add captions to identify people and places',
      'Upload high-quality original photos',
      'Encourage all members to contribute',
      'Create annual or milestone albums'
    ]
  },
  {
    id: 'roles',
    title: 'Family Roles',
    icon: Users,
    color: 'text-amber-500',
    bgColor: 'bg-amber-500/10',
    category: 'Organization',
    tier: 'seedling',
    description: 'Assign leadership roles to distribute family responsibilities.',
    howToUse: [
      'Go to the "Roles" tab on your family page',
      'View all available roles and their descriptions',
      'Click "Assign" to give a role to a member',
      'Members can hold multiple roles',
      'Visit the Role Guide for detailed information'
    ],
    bestPractices: [
      'Match roles to members\' interests and skills',
      'Start with key roles: Treasurer, Event Coordinator',
      'Rotate roles annually for fresh perspectives',
      'Provide guidance when assigning new roles',
      'Recognize and appreciate role holders'
    ],
    learnMore: '/role-guide'
  },
  {
    id: 'dues',
    title: 'Family Dues',
    icon: Coins,
    color: 'text-emerald-500',
    bgColor: 'bg-emerald-500/10',
    category: 'Finance',
    tier: 'growing',
    description: 'Manage family contributions, track payments, and allocate funds.',
    howToUse: [
      'Navigate to the "Dues" tab on your family page',
      'Set up your dues schedule (monthly, quarterly, annual)',
      'Define the contribution amount per member',
      'Track who has paid and who is overdue',
      'Allocate collected funds to specific events or goals'
    ],
    bestPractices: [
      'Be transparent about how dues are used',
      'Set reasonable amounts that work for everyone',
      'Send friendly reminders for overdue payments',
      'Keep detailed records of all transactions',
      'Report on dues usage at family gatherings'
    ]
  },
  {
    id: 'governance',
    title: 'Voting & Governance',
    icon: Vote,
    color: 'text-rose-500',
    bgColor: 'bg-rose-500/10',
    category: 'Organization',
    tier: 'growing',
    description: 'Make family decisions democratically through voting.',
    howToUse: [
      'Go to the "Governance" tab on your family page',
      'Create a new proposal with clear options',
      'Set a voting deadline',
      'Share the proposal with family members',
      'Review results and announce the decision'
    ],
    bestPractices: [
      'Use for significant family decisions',
      'Give adequate time for everyone to vote',
      'Explain the context and implications clearly',
      'Respect the outcome even if you disagree',
      'Document decisions for future reference'
    ]
  },
  {
    id: 'spiritual',
    title: 'Spiritual Heritage',
    icon: Heart,
    color: 'text-purple-500',
    bgColor: 'bg-purple-500/10',
    category: 'Legacy',
    tier: 'seedling',
    description: 'Honor and preserve your family\'s spiritual traditions.',
    howToUse: [
      'Click "Spiritual" in the family page header',
      'Add family prayers, blessings, or affirmations',
      'Share stories of faith from elders',
      'Create a gratitude board',
      'Document religious milestones and traditions'
    ],
    bestPractices: [
      'Involve elders in sharing spiritual wisdom',
      'Respect diverse beliefs within the family',
      'Create rituals for family gatherings',
      'Preserve prayers in multiple languages if relevant',
      'Connect spiritual practices to family values'
    ]
  },
  {
    id: 'timeline',
    title: 'Family Timeline',
    icon: Clock,
    color: 'text-amber-500',
    bgColor: 'bg-amber-500/10',
    category: 'Legacy',
    tier: 'free',
    description: 'Document your family\'s journey through milestones and memories.',
    howToUse: [
      'Click "Timeline" in the family page header',
      'Add entries for significant family events',
      'Include dates, descriptions, and photos',
      'View your family\'s history chronologically',
      'Let members contribute their own entries'
    ],
    bestPractices: [
      'Start with major historical events',
      'Include births, weddings, and anniversaries',
      'Add context and stories, not just dates',
      'Attach photos when available',
      'Interview elders for historical events'
    ]
  },
  {
    id: 'analytics',
    title: 'Family Analytics',
    icon: BarChart3,
    color: 'text-cyan-500',
    bgColor: 'bg-cyan-500/10',
    category: 'Insights',
    tier: 'flourishing',
    description: 'Gain insights into family engagement and activity.',
    howToUse: [
      'Click "Analytics" in the family page header',
      'View engagement metrics and trends',
      'See event attendance rates',
      'Identify most active family members',
      'Track dues collection progress'
    ],
    bestPractices: [
      'Review analytics monthly',
      'Use insights to improve engagement',
      'Celebrate high participation',
      'Reach out to less engaged members',
      'Adjust strategies based on data'
    ]
  },
  {
    id: 'resources',
    title: 'Professional Resources',
    icon: GraduationCap,
    color: 'text-indigo-500',
    bgColor: 'bg-indigo-500/10',
    category: 'Resources',
    tier: 'flourishing',
    description: 'Access family-focused professionals and experts.',
    howToUse: [
      'Click "Resources" in the main navigation',
      'Browse professionals by category',
      'Read profiles and reviews',
      'Contact professionals directly',
      'Book consultations when available'
    ],
    bestPractices: [
      'Research professionals before engaging',
      'Prepare questions in advance',
      'Share helpful resources with family',
      'Leave reviews to help other families',
      'Build relationships with trusted advisors'
    ],
    learnMore: '/professionals/directory'
  },
  {
    id: 'consultations',
    title: 'Consultations',
    icon: Phone,
    color: 'text-teal-500',
    bgColor: 'bg-teal-500/10',
    category: 'Resources',
    tier: 'flourishing',
    description: 'Schedule and manage expert consultations.',
    howToUse: [
      'Click "Consultations" in the main navigation',
      'View available consultation slots',
      'Book a time that works for you',
      'Receive confirmation and reminders',
      'Join at the scheduled time'
    ],
    bestPractices: [
      'Book well in advance for popular times',
      'Prepare an agenda or question list',
      'Take notes during the session',
      'Follow up on action items',
      'Share relevant insights with family'
    ]
  }
];

const CATEGORIES = [
  { id: 'all', label: 'All Features', icon: Home },
  { id: 'Core Features', label: 'Core', icon: Home },
  { id: 'Communication', label: 'Communication', icon: MessageCircle },
  { id: 'Memories', label: 'Memories', icon: Camera },
  { id: 'Organization', label: 'Organization', icon: Users },
  { id: 'Finance', label: 'Finance', icon: Coins },
  { id: 'Legacy', label: 'Legacy', icon: Heart },
  { id: 'Insights', label: 'Insights', icon: BarChart3 },
  { id: 'Resources', label: 'Resources', icon: GraduationCap }
];

const TIER_COLORS = {
  free: { bg: 'bg-gray-500/20', text: 'text-gray-400', label: 'Free' },
  seedling: { bg: 'bg-green-500/20', text: 'text-green-400', label: 'Seedling' },
  growing: { bg: 'bg-blue-500/20', text: 'text-blue-400', label: 'Growing' },
  flourishing: { bg: 'bg-purple-500/20', text: 'text-purple-400', label: 'Flourishing' }
};

export default function HelpCenter() {
  const [selectedFeature, setSelectedFeature] = useState('events');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  const filteredFeatures = FEATURES.filter(f => {
    const matchesCategory = selectedCategory === 'all' || f.category === selectedCategory;
    const matchesSearch = f.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          f.description.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const feature = FEATURES.find(f => f.id === selectedFeature);
  const Icon = feature?.icon || BookOpen;
  const tierInfo = TIER_COLORS[feature?.tier] || TIER_COLORS.free;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Link to="/dashboard" className="text-gray-400 hover:text-white">
            <ChevronLeft className="w-6 h-6" />
          </Link>
          <div className="flex-1">
            <h1 className="text-3xl font-bold text-white flex items-center gap-3">
              <BookOpen className="w-8 h-8 text-blue-400" />
              Help Center
            </h1>
            <p className="text-gray-400">Learn how to get the most out of Legacy Forge</p>
          </div>
        </div>

        {/* Search Bar */}
        <div className="relative mb-6">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search features..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-12 pr-4 py-3 bg-slate-800 border border-slate-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-blue-500"
            data-testid="help-search"
          />
        </div>

        {/* Category Pills */}
        <div className="flex flex-wrap gap-2 mb-6">
          {CATEGORIES.map(cat => {
            const CatIcon = cat.icon;
            return (
              <button
                key={cat.id}
                onClick={() => setSelectedCategory(cat.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition ${
                  selectedCategory === cat.id
                    ? 'bg-blue-500 text-white'
                    : 'bg-slate-700 text-gray-300 hover:bg-slate-600'
                }`}
                data-testid={`category-${cat.id}`}
              >
                <CatIcon className="w-4 h-4" />
                {cat.label}
              </button>
            );
          })}
        </div>

        <div className="grid lg:grid-cols-4 gap-6">
          {/* Feature List */}
          <div className="lg:col-span-1">
            <Card className="bg-slate-800 border-slate-700 sticky top-4">
              <CardHeader className="pb-3">
                <CardTitle className="text-white text-lg">Features</CardTitle>
              </CardHeader>
              <CardContent className="space-y-1 max-h-[60vh] overflow-y-auto">
                {filteredFeatures.map((f) => {
                  const FIcon = f.icon;
                  const fTier = TIER_COLORS[f.tier] || TIER_COLORS.free;
                  return (
                    <button
                      key={f.id}
                      onClick={() => setSelectedFeature(f.id)}
                      className={`w-full flex items-center gap-3 p-3 rounded-lg transition-all text-left ${
                        selectedFeature === f.id 
                          ? `${f.bgColor} border-2 border-${f.color.split('-')[1]}-500/30` 
                          : 'hover:bg-slate-700 border-2 border-transparent'
                      }`}
                      data-testid={`feature-${f.id}`}
                    >
                      <FIcon className={`w-5 h-5 ${f.color} flex-shrink-0`} />
                      <span className={`flex-1 ${selectedFeature === f.id ? 'text-white font-medium' : 'text-gray-300'} truncate`}>
                        {f.title}
                      </span>
                      {f.tier !== 'free' && (
                        <Lock className={`w-3 h-3 ${fTier.text}`} />
                      )}
                    </button>
                  );
                })}
              </CardContent>
            </Card>
          </div>

          {/* Feature Details */}
          <div className="lg:col-span-3 space-y-6">
            {feature && (
              <>
                {/* Feature Header */}
                <Card className={`${feature.bgColor} border-none`}>
                  <CardContent className="py-6">
                    <div className="flex items-start gap-4">
                      <div className={`p-4 rounded-xl bg-slate-900/50`}>
                        <Icon className={`w-10 h-10 ${feature.color}`} />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h2 className="text-2xl font-bold text-white">{feature.title}</h2>
                          <Badge className={`${tierInfo.bg} ${tierInfo.text} border-0`}>
                            {tierInfo.label}
                          </Badge>
                        </div>
                        <p className="text-gray-300 text-lg">{feature.description}</p>
                        <p className="text-gray-500 text-sm mt-2">Category: {feature.category}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* How To Use */}
                <Card className="bg-slate-800 border-slate-700">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-green-400" />
                      How to Use
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ol className="space-y-3">
                      {feature.howToUse.map((step, idx) => (
                        <li key={idx} className="flex items-start gap-3">
                          <span className="flex-shrink-0 w-6 h-6 bg-blue-500/20 text-blue-400 rounded-full flex items-center justify-center text-sm font-medium">
                            {idx + 1}
                          </span>
                          <span className="text-gray-300">{step}</span>
                        </li>
                      ))}
                    </ol>
                  </CardContent>
                </Card>

                {/* Best Practices */}
                <Card className="bg-slate-800 border-slate-700">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <Lightbulb className="w-5 h-5 text-yellow-400" />
                      Best Practices
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-3">
                      {feature.bestPractices.map((practice, idx) => (
                        <li key={idx} className="flex items-start gap-3">
                          <span className="text-yellow-400 mt-1">•</span>
                          <span className="text-gray-300">{practice}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>

                {/* Learn More */}
                {feature.learnMore && (
                  <Link
                    to={feature.learnMore}
                    className="block w-full p-4 bg-slate-700 hover:bg-slate-600 rounded-xl text-center text-blue-400 font-medium transition"
                  >
                    Learn More About {feature.title} →
                  </Link>
                )}
              </>
            )}
          </div>
        </div>

        {/* Quick Links */}
        <div className="mt-12 pt-8 border-t border-slate-700">
          <h3 className="text-xl font-semibold text-white mb-4">Quick Links</h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Link to="/role-guide" className="p-4 bg-slate-800 hover:bg-slate-700 rounded-xl transition group">
              <Users className="w-8 h-8 text-amber-500 mb-2 group-hover:scale-110 transition-transform" />
              <h4 className="text-white font-medium">Role Guide</h4>
              <p className="text-gray-400 text-sm">Understand family roles</p>
            </Link>
            <Link to="/pricing" className="p-4 bg-slate-800 hover:bg-slate-700 rounded-xl transition group">
              <Coins className="w-8 h-8 text-green-500 mb-2 group-hover:scale-110 transition-transform" />
              <h4 className="text-white font-medium">Pricing Plans</h4>
              <p className="text-gray-400 text-sm">Compare subscription tiers</p>
            </Link>
            <Link to="/professionals/directory" className="p-4 bg-slate-800 hover:bg-slate-700 rounded-xl transition group">
              <GraduationCap className="w-8 h-8 text-indigo-500 mb-2 group-hover:scale-110 transition-transform" />
              <h4 className="text-white font-medium">Professional Directory</h4>
              <p className="text-gray-400 text-sm">Find family experts</p>
            </Link>
            <Link to="/dashboard" className="p-4 bg-slate-800 hover:bg-slate-700 rounded-xl transition group">
              <Home className="w-8 h-8 text-blue-500 mb-2 group-hover:scale-110 transition-transform" />
              <h4 className="text-white font-medium">Dashboard</h4>
              <p className="text-gray-400 text-sm">Back to your families</p>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
